import Icon from "./Icon.jsx";

export default function BottomToolbar({
  selectedLayers,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onMirrorH,
  onMirrorV,
  onRecompose,
  onAddSiblings,
  onRemove,
  recomposeStatus,
}) {
  const count = selectedLayers.length;
  const single = count === 1 ? selectedLayers[0] : null;
  const memberCount = single?.members.length ?? 0;
  const recomposeDisabled = !single || recomposeStatus === "running";

  let recomposeLabel = single
    ? `Recompose · ${memberCount} piece${memberCount === 1 ? "" : "s"}`
    : "Recompose";
  if (recomposeStatus === "running") recomposeLabel = "Recomposing…";
  else if (recomposeStatus === "no-neighbors") recomposeLabel = "No more neighbors";

  return (
    <div className="flex h-16 items-center gap-2 border-t-2 border-paper-edge bg-paper px-4">
      <UndoButton kind="undo" disabled={!canUndo} onClick={onUndo} />
      <UndoButton kind="redo" disabled={!canRedo} onClick={onRedo} />

      <Divider />

      <span className="rounded-full bg-cyan-soft px-3 py-1 text-xs font-semibold text-ink">
        {count === 0
          ? "Nothing selected"
          : count === 1
          ? "1 selected"
          : `${count} selected`}
      </span>

      <ToolButton
        icon="flip-horizontal"
        label="Flip H"
        onClick={onMirrorH}
        disabled={count === 0}
        title="Flip horizontal"
      />
      <ToolButton
        icon="flip-vertical"
        label="Flip V"
        onClick={onMirrorV}
        disabled={count === 0}
        title="Flip vertical"
      />

      <Divider />

      <ToolButton
        icon="recompose"
        label={recomposeLabel}
        onClick={onRecompose}
        disabled={recomposeDisabled}
        tone="cyan"
        title={
          single
            ? "Add touching neighbors to this layer"
            : "Select a single layer to recompose"
        }
      />
      <ToolButton
        icon="siblings-to-palette"
        label="Siblings"
        onClick={onAddSiblings}
        disabled={!single}
        title={
          single
            ? "Replace palette with siblings from this segment's source image"
            : "Select a single layer first"
        }
      />

      <div className="ml-auto" />

      <ToolButton
        icon="delete"
        label="Delete"
        onClick={onRemove}
        disabled={count === 0}
        tone="coral"
        title={count === 1 ? "Delete this layer" : `Delete ${count} layers`}
      />
    </div>
  );
}

function Divider() {
  return <div className="mx-1 h-7 w-0.5 rounded-full bg-paper-edge" />;
}

function UndoButton({ kind, disabled, onClick }) {
  const isUndo = kind === "undo";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={isUndo ? "Undo (⌘Z)" : "Redo (⌘⇧Z)"}
      aria-label={isUndo ? "Undo" : "Redo"}
      className="flex h-10 w-10 items-center justify-center rounded-2xl border-2 border-ink/20 bg-white text-base font-bold text-ink transition hover:border-ink hover:bg-paper-deep disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <span aria-hidden="true">{isUndo ? "↺" : "↻"}</span>
    </button>
  );
}

function ToolButton({ icon, label, onClick, disabled, title, tone = "default" }) {
  const tones = {
    default: "bg-white border-ink/20 text-ink hover:border-ink hover:bg-paper-deep",
    cyan: "bg-cyan-soft border-ink text-ink hover:bg-cyan-bright shadow-plump",
    coral: "bg-coral-soft border-ink text-ink hover:bg-coral shadow-plump",
  };
  const cls = `flex items-center gap-2 rounded-2xl border-2 px-3 py-2 text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone] || tones.default}`;
  return (
    <button onClick={onClick} disabled={disabled} title={title} className={cls}>
      <Icon name={icon} size={22} alt="" />
      {label}
    </button>
  );
}
